import React, { useState } from 'react';

import { blobUrl } from 'constants/Common';

import { FormattedMessage, useIntl } from 'react-intl';
import { useSearchParams } from 'react-router';

import { Button } from 'components/buttons/Button';
import { TextField } from 'components/inputs/TextField.react';

import { useResetPassword } from '../hooks/useResetPassword';

import { BackToLoginLink } from './common/BackToLoginLink';
import { ErrorMessage } from './common/ErrorMessage';
import { IllustrationBanner } from './common/IllustrationBanner';

interface ResetPasswordViewProps {
    isSetPassword?: boolean;
}

// Extract the raw, URL-encoded code value from the URL.  ASP.NET Identity
// tokens contain '+' characters that are URL-encoded as '%2B'.  React
// Router's useSearchParams decodes those to '+', but the form encoding
// of the request then treats '+' as a space — breaking token validation on the
// backend.  This mirrors how the old login parsed the URL.
const getRawCodeFromUrl = (): string => {
    const paramsString = window.location.href.split('?code=')[1];
    const paramsArray = paramsString ? paramsString.split('&email=') : null;
    return paramsArray ? paramsArray[0] : '';
};

export const ResetPasswordView = ({ isSetPassword = false }: ResetPasswordViewProps) => {
    const [searchParams] = useSearchParams();

    const email = searchParams.get('email') ?? '';
    const code = getRawCodeFromUrl();

    const {
        step,
        isValidating,
        isResetting,
        resetError,
        submitPassword,
        useBreachedPassword,
        chooseAnotherPassword,
    } = useResetPassword({ email, code });

    if (isValidating || step === 'loading') {
        return (
            <div className="tw-flex tw-justify-center tw-py-48">
                <div className="tw-animate-spin tw-h-24 tw-w-24 tw-border-2 tw-border-interactive tw-border-t-transparent tw-rounded-full" />
            </div>
        );
    }

    if (step === 'expired') {
        return <ExpiredLinkView />;
    }

    if (step === 'success') {
        return <SuccessView isSetPassword={isSetPassword} />;
    }

    if (step === 'breached') {
        return (
            <BreachedWarningView
                onChooseAnother={chooseAnotherPassword}
                onUseAnyway={useBreachedPassword}
            />
        );
    }

    // step === 'form'
    return (
        <PasswordForm
            isSetPassword={isSetPassword}
            isLoading={isResetting}
            error={resetError}
            onSubmit={submitPassword}
        />
    );
};

// -------------------------------------------------------------------
// Sub-views
// -------------------------------------------------------------------

interface PasswordFormProps {
    isSetPassword: boolean;
    isLoading: boolean;
    error?: string;
    onSubmit: (password: string) => void;
}

const PasswordForm = ({ isSetPassword, isLoading, error, onSubmit }: PasswordFormProps) => {
    const intl = useIntl();
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [validationError, setValidationError] = useState('');

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (String(password) !== String(confirmPassword)) {
            setValidationError(
                intl.formatMessage({
                    id: 'login.resetPassword.mismatch',
                    defaultMessage: 'Passwords do not match',
                }),
            );
            return;
        }
        if (password.length < 8) {
            setValidationError(
                intl.formatMessage({
                    id: 'login.resetPassword.tooShort',
                    defaultMessage: 'Password must be at least 8 characters',
                }),
            );
            return;
        }
        setValidationError('');
        onSubmit(password);
    };

    const titleId = isSetPassword ? 'login.setPassword.title' : 'login.resetPassword.title';
    const titleDefault = isSetPassword ? 'Set your password' : 'Reset your password';

    return (
        <div>
            <IllustrationBanner src={`${blobUrl}login/pr-enter-password.png`} />
            <h1 className="tw-text-xl tw-font-semibold tw-mb-8">
                <FormattedMessage id={titleId} defaultMessage={titleDefault} />
            </h1>
            <p className="tw-text-neutral-subdued tw-text-s tw-mb-24">
                <FormattedMessage
                    id="login.resetPassword.description"
                    defaultMessage="Enter your new password below."
                />
            </p>

            <form onSubmit={handleSubmit}>
                <div className="tw-mb-12">
                    <TextField
                        name="password"
                        password
                        label={intl.formatMessage({
                            id: 'login.resetPassword.newPassword',
                            defaultMessage: 'New password',
                        })}
                        placeholderText={intl.formatMessage({
                            id: 'login.resetPassword.newPassword.placeholder',
                            defaultMessage: 'Enter new password',
                        })}
                        value={password}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            setPassword(e.target.value)
                        }
                        autoFocus
                        autoComplete="new-password"
                        className=""
                    />
                </div>
                <TextField
                    name="confirmPassword"
                    password
                    label={intl.formatMessage({
                        id: 'login.resetPassword.confirmPassword',
                        defaultMessage: 'Confirm password',
                    })}
                    placeholderText={intl.formatMessage({
                        id: 'login.resetPassword.confirmPassword.placeholder',
                        defaultMessage: 'Confirm new password',
                    })}
                    value={confirmPassword}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setConfirmPassword(e.target.value)
                    }
                    autoComplete="new-password"
                    className=""
                />
                <ErrorMessage message={validationError || error} />
                <div className="tw-mt-16">
                    <Button
                        type="submit"
                        variant="primary"
                        disabled={isLoading}
                        wrapperClasses="tw-w-full"
                    >
                        <FormattedMessage
                            id="login.resetPassword.submit"
                            defaultMessage={isSetPassword ? 'Set password' : 'Reset password'}
                        />
                    </Button>
                </div>
            </form>
            <BackToLoginLink />
        </div>
    );
};

const SuccessView = ({ isSetPassword }: { isSetPassword: boolean }) => {
    const successDefault = isSetPassword
        ? 'Password set successfully!'
        : 'Password changed successfully!';

    return (
        <div>
            <IllustrationBanner src={`${blobUrl}login/pr-password-updated.png`} />
            <h2 className="tw-text-l tw-font-semibold tw-mb-8">
                <FormattedMessage
                    id="login.resetPassword.success.title"
                    defaultMessage={successDefault}
                />
            </h2>
            <p className="tw-text-neutral-subdued tw-text-s tw-mb-16">
                <FormattedMessage
                    id="login.resetPassword.success.description"
                    defaultMessage="You can now sign in with your new password."
                />
            </p>
            <Button
                variant="primary"
                wrapperClasses="tw-w-full"
                onClick={() => {
                    window.location.href = '/';
                }}
            >
                <FormattedMessage
                    id="login.resetPassword.success.continue"
                    defaultMessage="Continue to SalesScreen"
                />
            </Button>
        </div>
    );
};

const BreachedWarningView = ({
    onChooseAnother,
    onUseAnyway,
}: {
    onChooseAnother: () => void;
    onUseAnyway: () => void;
}) => (
    <div>
        <IllustrationBanner src={`${blobUrl}login/pr-breach-detected.png`} />
        <h2 className="tw-text-l tw-font-semibold tw-mb-8">
            <FormattedMessage
                id="login.resetPassword.breached.title"
                defaultMessage="Password found in data breach"
            />
        </h2>
        <p className="tw-text-neutral-subdued tw-text-s tw-mb-16">
            <FormattedMessage
                id="login.resetPassword.breached.description"
                defaultMessage="This password has appeared in a data breach. We recommend choosing a different password for your security."
            />
        </p>
        <div className="tw-flex tw-flex-col tw-gap-8">
            <Button variant="primary" wrapperClasses="tw-w-full" onClick={onChooseAnother}>
                <FormattedMessage
                    id="login.resetPassword.breached.chooseAnother"
                    defaultMessage="Choose another password"
                />
            </Button>
            <Button variant="secondary" wrapperClasses="tw-w-full" onClick={onUseAnyway}>
                <FormattedMessage
                    id="login.resetPassword.breached.useAnyway"
                    defaultMessage="Use this password anyway"
                />
            </Button>
        </div>
    </div>
);

const ExpiredLinkView = () => (
    <div>
        <h2 className="tw-text-l tw-font-semibold tw-mb-8">
            <FormattedMessage
                id="login.resetPassword.expired.title"
                defaultMessage="Link expired"
            />
        </h2>
        <p className="tw-text-neutral-subdued tw-text-s tw-mb-16">
            <FormattedMessage
                id="login.resetPassword.expired.description"
                defaultMessage="This password reset link has expired. Please request a new one from the login page."
            />
        </p>
        <BackToLoginLink />
    </div>
);
